import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import { ArrowLeft, Download, Save, Plane, Building2, User, Calendar, FileText, AlertCircle, Trash2 } from "lucide-react";

const ALL_STATUSES = ["Open", "Under Review", "Quoted", "Closed"];
const INCOTERMS = ["", "EXW", "FCA", "FAS", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
const CONDITIONS = ["", "NE", "NS", "OH", "SV", "RE", "AS"];

const RfqDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [rfq, setRfq] = useState(null);
    const [quotes, setQuotes] = useState({}); // itemId -> {unit_price, available_quantity, offered_lead_time}
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/rfq/${id}`);
            setRfq(data);
            // hydrate quote state for admin form
            const q = {};
            data.items.forEach((it) => {
                q[it.id] = it.quotation
                    ? {
                          unit_price: String(it.quotation.unit_price ?? ""),
                          available_quantity: String(it.quotation.available_quantity ?? ""),
                          offered_lead_time: it.quotation.offered_lead_time ?? "",
                          incoterm: it.quotation.incoterm ?? "",
                          offered_condition: it.quotation.offered_condition ?? "",
                          estimated_delivery_cost: String(it.quotation.estimated_delivery_cost ?? ""),
                      }
                    : { unit_price: "", available_quantity: "", offered_lead_time: "", incoterm: "", offered_condition: "", estimated_delivery_cost: "" };
            });
            setQuotes(q);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const updateStatus = async (status) => {
        if (!isAdmin || status === rfq.status) return;
        try {
            const { data } = await api.patch(`/rfq/${id}/status`, { status });
            setRfq(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const saveQuotations = async () => {
        setErr("");
        setBusy(true);
        try {
            const payload = {
                items: Object.entries(quotes)
                    .filter(([, v]) => v.unit_price !== "" || v.available_quantity !== "" || v.offered_lead_time !== "" || v.incoterm !== "" || v.offered_condition !== "" || v.estimated_delivery_cost !== "")
                    .map(([item_id, v]) => ({
                        item_id,
                        unit_price: Number(v.unit_price || 0),
                        available_quantity: Number(v.available_quantity || 0),
                        offered_lead_time: v.offered_lead_time,
                        incoterm: v.incoterm || "",
                        offered_condition: v.offered_condition || "",
                        estimated_delivery_cost: Number(v.estimated_delivery_cost || 0),
                    })),
            };
            const { data } = await api.put(`/rfq/${id}/quotations`, payload);
            setRfq(data);
            setSavedAt(new Date());
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const exportExcel = async () => {
        try {
            const res = await api.get(`/rfq/${id}/export`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `RFQ-${rfq.ticket_number}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const deleteRfq = async () => {
        const label = isAdmin ? "delete" : "cancel";
        if (!window.confirm(`Are you sure you want to ${label} RFQ ${rfq.ticket_number}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/rfq/${id}`);
            navigate("/portal/rfqs", { replace: true });
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    if (!rfq) {
        return (
            <div className="font-body text-sm" style={{ color: "var(--ns-muted)" }} data-testid="rfq-loading">
                {err || "Loading RFQ…"}
            </div>
        );
    }

    return (
        <div data-testid="rfq-detail-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 font-body text-sm mb-6 hover:underline" style={{ color: "var(--ns-muted)" }}>
                <ArrowLeft size={14} /> Back
            </button>

            {/* Header */}
            <div className="bg-white border border-[var(--ns-border)] p-7 lg:p-8 mb-8">
                <div className="flex flex-wrap items-start justify-between gap-6">
                    <div>
                        <div className="font-body text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: "var(--ns-muted)" }}>
                            Request For Quotation
                        </div>
                        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight tabular-nums" style={{ color: "var(--ns-ink)" }} data-testid="rfq-ticket">
                            {rfq.ticket_number}
                        </h1>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                        <StatusBadge status={rfq.status} />
                        <div className="flex flex-wrap items-center gap-2">
                            <button onClick={exportExcel} className="ns-btn-primary" data-testid="export-rfq-excel">
                                <Download size={15} /> Export RFQ Package
                            </button>
                            {(isAdmin || rfq.status === "Open") && (
                                <button
                                    onClick={deleteRfq}
                                    className="inline-flex items-center gap-2 px-4 py-3 font-body text-sm border transition-colors hover:border-[#a8201d] hover:text-[#a8201d]"
                                    style={{ color: "var(--ns-muted)", borderColor: "var(--ns-border)" }}
                                    data-testid="delete-rfq"
                                    title={isAdmin ? "Delete RFQ permanently" : "Cancel this RFQ"}
                                >
                                    <Trash2 size={14} /> {isAdmin ? "Delete RFQ" : "Cancel RFQ"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Meta strip */}
                <div className="mt-8 pt-8 border-t border-[var(--ns-border)] grid grid-cols-2 lg:grid-cols-4 gap-6">
                    <Meta icon={Plane} label="Aircraft Type" value={rfq.aircraft_type} />
                    <Meta icon={Calendar} label="Date" value={(rfq.creation_date || "").slice(0, 10)} mono />
                    <Meta icon={User} label="Client" value={rfq.client_name || "—"} />
                    <Meta icon={Building2} label="Company" value={rfq.client_company || "—"} />
                </div>
                {rfq.notes && (
                    <div className="mt-6 pt-6 border-t border-[var(--ns-border)]">
                        <div className="font-body text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: "var(--ns-muted)" }}>
                            Notes
                        </div>
                        <p className="font-body text-sm" style={{ color: "var(--ns-body)" }}>{rfq.notes}</p>
                    </div>
                )}

                {/* Status switcher (admin only) */}
                {isAdmin && (
                    <div className="mt-6 pt-6 border-t border-[var(--ns-border)] flex flex-wrap items-center gap-2">
                        <span className="font-body text-[11px] uppercase tracking-[0.22em] mr-3" style={{ color: "var(--ns-muted)" }}>
                            Change status:
                        </span>
                        {ALL_STATUSES.map((s) => {
                            const active = s === rfq.status;
                            return (
                                <button
                                    key={s}
                                    onClick={() => updateStatus(s)}
                                    disabled={active}
                                    data-testid={`status-set-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`px-3 py-2 font-body text-[11px] uppercase tracking-[0.14em] border transition-colors ${
                                        active
                                            ? "bg-[var(--ns-ink)] text-white border-[var(--ns-ink)] cursor-default"
                                            : "bg-white border-[var(--ns-border)] hover:border-[var(--ns-ink)]"
                                    }`}
                                    style={!active ? { color: "var(--ns-body)" } : {}}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {err && (
                <div className="mb-6 px-4 py-3 border bg-[#fff3f1] border-[#e6c8c2] font-body text-sm flex items-start gap-2" style={{ color: "#7a2218" }}>
                    <AlertCircle size={16} className="mt-0.5" />
                    <span>{err}</span>
                </div>
            )}

            {/* Client Request table */}
            <Section title="Client Request" subtitle="Material lines submitted by the client" testid="section-client-request">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr style={{ background: "#fafaf8" }} className="border-b border-[var(--ns-border)]">
                                <Th w="50px">#</Th>
                                <Th>Part Number</Th>
                                <Th>Alternate P/N</Th>
                                <Th>NSN</Th>
                                <Th>Aircraft Model</Th>
                                <Th align="center" w="160px">Acceptable Conditions</Th>
                                <Th align="center" w="100px">Qty</Th>
                                <Th>Requested LT</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {rfq.items.map((it, i) => (
                                <tr key={it.id} className="border-b border-[var(--ns-border)] last:border-0">
                                    <td className="px-4 py-3 text-center font-body text-sm tabular-nums" style={{ color: "var(--ns-muted)" }}>
                                        {String(i + 1).padStart(2, "0")}
                                    </td>
                                    <Td><span className="font-display font-semibold" style={{ color: "var(--ns-ink)" }}>{it.part_number}</span></Td>
                                    <Td>{it.alternate_part_number || "—"}</Td>
                                    <Td>{it.nsn || "—"}</Td>
                                    <Td>{it.aircraft_model || "—"}</Td>
                                    <Td align="center">
                                        {(it.acceptable_conditions && it.acceptable_conditions.length > 0) ? (
                                            <div className="flex flex-wrap gap-1 justify-center">
                                                {it.acceptable_conditions.map((c) => (
                                                    <span key={c} className="inline-flex items-center px-1.5 py-0.5 font-display text-[10px] font-semibold tracking-[0.1em]" style={{ background: "#eef2f7", color: "var(--ns-ink)" }}>{c}</span>
                                                ))}
                                            </div>
                                        ) : "—"}
                                    </Td>
                                    <Td align="center"><span className="tabular-nums">{it.quantity}</span></Td>
                                    <Td>{it.requested_lead_time || "—"}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Supplier Quotation table */}
            <Section
                title="Supplier Quotation"
                subtitle={isAdmin ? "Fill the commercial offer per line" : "Commercial offer provided by Nador Sage"}
                testid="section-supplier-quotation"
            >
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr style={{ background: "#fafaf8" }} className="border-b border-[var(--ns-border)]">
                                <Th w="50px">#</Th>
                                <Th>Part Number</Th>
                                <Th align="center" w="110px">Condition</Th>
                                <Th align="center" w="150px">Unit Price (USD)</Th>
                                <Th align="center" w="120px">Available Qty</Th>
                                <Th w="140px">Offered Lead Time</Th>
                                <Th align="center" w="110px">Incoterm</Th>
                                <Th align="center" w="150px">Est. Delivery Cost</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {rfq.items.map((it, i) => {
                                const v = quotes[it.id] || { unit_price: "", available_quantity: "", offered_lead_time: "", incoterm: "", offered_condition: "", estimated_delivery_cost: "" };
                                return (
                                    <tr key={it.id} className="border-b border-[var(--ns-border)] last:border-0" data-testid={`quote-row-${i}`}>
                                        <td className="px-4 py-3 text-center font-body text-sm tabular-nums" style={{ color: "var(--ns-muted)" }}>
                                            {String(i + 1).padStart(2, "0")}
                                        </td>
                                        <Td><span className="font-display font-semibold" style={{ color: "var(--ns-ink)" }}>{it.part_number}</span></Td>

                                        {isAdmin ? (
                                            <>
                                                <Td>
                                                    <select
                                                        value={v.offered_condition}
                                                        onChange={(e) => setQuotes((q) => ({ ...q, [it.id]: { ...q[it.id], offered_condition: e.target.value } }))}
                                                        data-testid={`quote-${i}-condition`}
                                                        className="w-full bg-white border border-[var(--ns-border)] px-3 py-2 font-body text-sm focus:outline-none focus:border-[var(--ns-ink)] transition-colors"
                                                        style={{ color: "var(--ns-ink)" }}
                                                    >
                                                        {CONDITIONS.map((t) => (
                                                            <option key={t} value={t}>{t || "—"}</option>
                                                        ))}
                                                    </select>
                                                </Td>
                                                <Td><QInput value={v.unit_price} type="number" step="0.01" min="0" onChange={(val) => setQuotes((q) => ({ ...q, [it.id]: { ...q[it.id], unit_price: val } }))} testid={`quote-${i}-price`} /></Td>
                                                <Td><QInput value={v.available_quantity} type="number" min="0" onChange={(val) => setQuotes((q) => ({ ...q, [it.id]: { ...q[it.id], available_quantity: val } }))} testid={`quote-${i}-qty`} /></Td>
                                                <Td><QInput value={v.offered_lead_time} placeholder="e.g. 21 days" onChange={(val) => setQuotes((q) => ({ ...q, [it.id]: { ...q[it.id], offered_lead_time: val } }))} testid={`quote-${i}-lt`} /></Td>
                                                <Td>
                                                    <select
                                                        value={v.incoterm}
                                                        onChange={(e) => setQuotes((q) => ({ ...q, [it.id]: { ...q[it.id], incoterm: e.target.value } }))}
                                                        data-testid={`quote-${i}-incoterm`}
                                                        className="w-full bg-white border border-[var(--ns-border)] px-3 py-2 font-body text-sm focus:outline-none focus:border-[var(--ns-ink)] transition-colors"
                                                        style={{ color: "var(--ns-ink)" }}
                                                    >
                                                        {INCOTERMS.map((t) => (
                                                            <option key={t} value={t}>{t || "—"}</option>
                                                        ))}
                                                    </select>
                                                </Td>
                                                <Td><QInput value={v.estimated_delivery_cost} type="number" step="0.01" min="0" placeholder="0.00" onChange={(val) => setQuotes((q) => ({ ...q, [it.id]: { ...q[it.id], estimated_delivery_cost: val } }))} testid={`quote-${i}-delivery`} /></Td>
                                            </>
                                        ) : (
                                            <>
                                                <Td align="center">
                                                    {it.quotation?.offered_condition ? (
                                                        <span className="inline-flex items-center px-2 py-1 font-display font-semibold text-[11px] tracking-[0.1em]" style={{ background: "#eef2f7", color: "var(--ns-ink)" }}>
                                                            {it.quotation.offered_condition}
                                                        </span>
                                                    ) : "—"}
                                                </Td>
                                                <Td align="center">
                                                    {it.quotation ? (
                                                        <span className="font-display font-semibold tabular-nums" style={{ color: "var(--ns-ink)" }}>
                                                            $ {Number(it.quotation.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    ) : "—"}
                                                </Td>
                                                <Td align="center">{it.quotation ? <span className="tabular-nums">{it.quotation.available_quantity}</span> : "—"}</Td>
                                                <Td>{it.quotation?.offered_lead_time || "—"}</Td>
                                                <Td align="center">
                                                    {it.quotation?.incoterm ? (
                                                        <span className="inline-flex items-center px-2.5 py-1 font-display font-semibold text-[12px] tracking-[0.08em]" style={{ background: "#eef2f7", color: "var(--ns-ink)" }}>
                                                            {it.quotation.incoterm}
                                                        </span>
                                                    ) : "—"}
                                                </Td>
                                                <Td align="center">
                                                    {it.quotation && Number(it.quotation.estimated_delivery_cost) > 0 ? (
                                                        <span className="font-display font-semibold tabular-nums" style={{ color: "var(--ns-ink)" }}>
                                                            $ {Number(it.quotation.estimated_delivery_cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    ) : "—"}
                                                </Td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="px-6 lg:px-8 py-4 border-t border-[var(--ns-border)] bg-[#fafaf8]">
                    <p className="font-body text-xs italic" style={{ color: "var(--ns-muted)" }}>
                        Prices exclude duties and VAT where applicable. Estimated delivery cost is indicative and subject to final routing confirmation.
                    </p>
                </div>

                {isAdmin && (
                    <div className="px-6 py-5 border-t border-[var(--ns-border)] flex flex-wrap items-center justify-between gap-3">
                        <div className="font-body text-xs" style={{ color: "var(--ns-muted)" }}>
                            {savedAt ? `Saved at ${savedAt.toLocaleTimeString()}` : "Unsaved changes will be lost when you leave this page."}
                        </div>
                        <button onClick={saveQuotations} disabled={busy} className="ns-btn-primary" data-testid="save-quotations">
                            <Save size={15} />
                            {busy ? "Saving…" : "Save Quotations"}
                        </button>
                    </div>
                )}
            </Section>
        </div>
    );
};

const Section = ({ title, subtitle, children, testid }) => (
    <section className="bg-white border border-[var(--ns-border)] mb-8" data-testid={testid}>
        <div className="px-6 lg:px-8 py-5 border-b border-[var(--ns-border)]">
            <h2 className="font-display text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--ns-ink)" }}>
                {title}
            </h2>
            {subtitle && <p className="font-body text-xs mt-1" style={{ color: "var(--ns-muted)" }}>{subtitle}</p>}
        </div>
        {children}
    </section>
);

const Th = ({ children, align = "left", w }) => (
    <th className={`px-4 py-3.5 font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-${align}`} style={{ color: "var(--ns-muted)", width: w }}>
        {children}
    </th>
);
const Td = ({ children, align = "left" }) => (
    <td className={`px-4 py-3 font-body text-sm text-${align}`} style={{ color: "var(--ns-body)" }}>{children}</td>
);

const QInput = ({ value, onChange, testid, ...rest }) => (
    <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        {...rest}
        className="w-full bg-white border border-[var(--ns-border)] px-3 py-2 font-body text-sm focus:outline-none focus:border-[var(--ns-ink)] transition-colors tabular-nums"
        style={{ color: "var(--ns-ink)" }}
    />
);

const Meta = ({ icon: Icon, label, value, mono }) => (
    <div>
        <div className="font-body text-[10px] uppercase tracking-[0.22em] mb-1.5" style={{ color: "var(--ns-muted-2)" }}>{label}</div>
        <div className="flex items-center gap-2">
            <Icon size={14} style={{ color: "var(--ns-muted)" }} />
            <span className={`font-body text-sm ${mono ? "tabular-nums" : ""}`} style={{ color: "var(--ns-ink)" }}>{value}</span>
        </div>
    </div>
);

export default RfqDetail;
